import { motion } from "framer-motion";
import { Phone, ShieldCheck, Star, Clock } from "lucide-react";
import { SITE, IMAGES } from "../../data/site";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-[#F9F7F4] overflow-hidden grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-end">
          {/* Left — editorial headline */}
          <div className="col-span-12 lg:col-span-7 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-[#C05746]" />
              <span className="text-[#C05746] text-xs md:text-sm font-semibold tracking-[0.24em] uppercase">
                Family-Owned · Since {SITE.founded}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="font-display text-[#1C1B1A] text-[3.25rem] sm:text-[4.25rem] md:text-[5.25rem] lg:text-[6.25rem] leading-[0.95] tracking-[-0.03em]"
              data-testid="hero-headline"
            >
              35 Years.
              <br />
              Zero <span className="font-serif-accent italic text-[#C05746]">retreats</span>.
              <br />
              Total protection.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-8 max-w-xl text-[#5C5A55] text-lg md:text-xl leading-relaxed"
            >
              Pennsylvania & Maryland's most trusted termite experts. One call
              and you talk to a real human — most inspections scheduled within
              days, not weeks.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                href={SITE.phoneHref}
                data-testid="hero-call-button"
                className="cta-shimmer group inline-flex items-center gap-4 bg-[#1E2C25] hover:bg-[#2A3B32] text-[#F9F7F4] pl-7 pr-8 py-5 md:py-6 transition-all hover:-translate-y-0.5"
              >
                <span className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-[#C05746] text-[#F9F7F4] -ml-3 -my-3 md:-my-4">
                  <Phone size={20} strokeWidth={2.5} />
                </span>
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-[#F9F7F4]/60">
                    Call now — talk to a human
                  </span>
                  <span className="font-display text-2xl md:text-3xl tracking-tight">
                    {SITE.phoneDisplay}
                  </span>
                </span>
              </a>
              <div className="text-xs md:text-sm text-[#5C5A55]">
                <div className="font-semibold text-[#1C1B1A]">Open now</div>
                <div>{SITE.hours}</div>
              </div>
            </motion.div>

            {/* Trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 md:mt-14 grid grid-cols-3 max-w-lg border-t border-[#E5DFD5]"
            >
              {[
                { icon: ShieldCheck, label: "A+ BBB", sub: "Accredited" },
                { icon: Star, label: "4.8★", sub: "148+ reviews" },
                { icon: Clock, label: "Fast", sub: "Scheduling" },
              ].map((t) => (
                <div key={t.label} className="pt-5 pr-4">
                  <t.icon size={18} className="text-[#C05746] mb-2" />
                  <div className="font-display text-[#1C1B1A] text-base leading-tight">{t.label}</div>
                  <div className="text-xs text-[#5C5A55]">{t.sub}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — framed cinematic image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="col-span-12 lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] bg-[#1E2C25] overflow-hidden">
              <img
                src={IMAGES.hero}
                alt="Elegant suburban home protected by ATC Pest Control termite treatment"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E2C25]/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 text-[#F9F7F4]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#F9F7F4]/70">Est. {SITE.founded}</div>
                  <div className="font-display text-2xl md:text-3xl mt-1 leading-tight">
                    PA &amp; MD
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#F9F7F4]/70">File Nº</div>
                  <div className="font-display text-2xl md:text-3xl mt-1 leading-tight">001</div>
                </div>
              </div>
              <div className="absolute top-6 left-6 flex items-center gap-2 bg-[#F9F7F4]/10 backdrop-blur-md border border-[#F9F7F4]/20 px-3 py-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C05746] animate-pulse" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#F9F7F4]">Now Accepting Calls</span>
              </div>
            </div>
            <div className="hidden lg:block absolute -left-6 -bottom-6 w-28 h-28 bg-[#C05746] z-[-1]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
